import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { ArrowLeft, ArrowRight } from "lucide-react";

/** Formulários da Fase 2: Exame, Transporte, Acompanhante.
 * Backend já existe em /api/fp/requests/{exame|transporte|acompanhante}.
 * Cada rota reaproveita este componente com `kind` diferente. */
export default function FPRequestKindForm({ kind }) {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    beneficiary_id: params.get("beneficiary_id") || "",
    // exame
    exam_type: "", medical_order_url: "", city: "", preferred_lab: "",
    desired_date: "", notes: "",
    // transporte
    origin: "", destination: "", date: "", time: "",
    needs_companion: false, mobility: "independente",
    // acompanhante
    location: "", estimated_duration_min: "",
  });

  useEffect(() => {
    api.get("/fp/beneficiaries").then((r) => setBeneficiaries(r.data));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const config = {
    exame: {
      title: "Solicitar exame",
      subtitle: "A Central RAF irá coordenar o agendamento do exame no laboratório mais adequado.",
      endpoint: "/fp/requests/exame",
      payload: (f) => ({
        beneficiary_id: f.beneficiary_id, exam_type: f.exam_type,
        medical_order_url: f.medical_order_url || null, city: f.city,
        preferred_lab: f.preferred_lab, desired_date: f.desired_date,
        notes: f.notes,
      }),
      validate: (f) => f.beneficiary_id && f.exam_type,
    },
    transporte: {
      title: "Solicitar transporte",
      subtitle: "Transporte ida e volta para consultas e exames.",
      endpoint: "/fp/requests/transporte",
      payload: (f) => ({
        beneficiary_id: f.beneficiary_id, origin: f.origin,
        destination: f.destination, date: f.date, time: f.time,
        needs_companion: f.needs_companion, mobility: f.mobility,
        notes: f.notes,
      }),
      validate: (f) => f.beneficiary_id && f.origin && f.destination && f.date && f.time,
    },
    acompanhante: {
      title: "Solicitar acompanhante",
      subtitle: "Alguém para acompanhar o beneficiário em consulta, exame ou procedimento.",
      endpoint: "/fp/requests/acompanhante",
      payload: (f) => ({
        beneficiary_id: f.beneficiary_id, date: f.date, time: f.time,
        location: f.location,
        estimated_duration_min: f.estimated_duration_min ? Number(f.estimated_duration_min) : null,
        notes: f.notes,
      }),
      validate: (f) => f.beneficiary_id && f.date && f.time && f.location,
    },
  }[kind];

  const submit = async (e) => {
    e.preventDefault();
    if (!config.validate(form)) return toast.error("Preencha os campos obrigatórios");
    setSaving(true);
    try {
      const { data } = await api.post(config.endpoint, config.payload(form));
      toast.success("Solicitação enviada!");
      nav(`/filho-presente/solicitacoes/${data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao enviar");
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8" data-testid={`fp-req-${kind}-form`}>
      <Link to="/filho-presente/painel" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#0B2B62] mb-4">
        <ArrowLeft className="w-4 h-4" /> Voltar
      </Link>
      <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#0B2B62] mb-2">{config.title}</h1>
      <p className="text-slate-600 text-sm mb-6">{config.subtitle}</p>

      <form onSubmit={submit} className="bg-white border border-slate-200 rounded-xl p-6 space-y-5">
        <F label="Para qual familiar? *">
          <select value={form.beneficiary_id} onChange={(e) => set("beneficiary_id", e.target.value)} required
            data-testid={`fp-${kind}-beneficiary`} className="input">
            <option value="">Selecione...</option>
            {beneficiaries.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </F>

        {kind === "exame" && (
          <>
            <F label="Tipo do exame *"><input value={form.exam_type} onChange={(e) => set("exam_type", e.target.value)} required data-testid="fp-exame-type" placeholder="Hemograma, ressonância, ecocardiograma..." className="input" /></F>
            <div className="grid sm:grid-cols-2 gap-4">
              <F label="Cidade"><input value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="fp-exame-city" className="input" /></F>
              <F label="Laboratório de preferência"><input value={form.preferred_lab} onChange={(e) => set("preferred_lab", e.target.value)} className="input" /></F>
            </div>
            <F label="Data desejada"><input type="date" value={form.desired_date} onChange={(e) => set("desired_date", e.target.value)} min={new Date().toISOString().slice(0, 10)} data-testid="fp-exame-date" className="input" /></F>
            <F label="Link do pedido médico (opcional)"><input value={form.medical_order_url} onChange={(e) => set("medical_order_url", e.target.value)} placeholder="URL do pedido, se já tiver" className="input" /></F>
          </>
        )}

        {kind === "transporte" && (
          <>
            <div className="grid sm:grid-cols-2 gap-4">
              <F label="Origem *"><input value={form.origin} onChange={(e) => set("origin", e.target.value)} required data-testid="fp-transp-origin" placeholder="Endereço de partida" className="input" /></F>
              <F label="Destino *"><input value={form.destination} onChange={(e) => set("destination", e.target.value)} required data-testid="fp-transp-dest" placeholder="Clínica, hospital..." className="input" /></F>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <F label="Data *"><input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} min={new Date().toISOString().slice(0, 10)} required data-testid="fp-transp-date" className="input" /></F>
              <F label="Horário *"><input type="time" value={form.time} onChange={(e) => set("time", e.target.value)} required data-testid="fp-transp-time" className="input" /></F>
            </div>
            <F label="Mobilidade">
              <select value={form.mobility} onChange={(e) => set("mobility", e.target.value)} data-testid="fp-transp-mobility" className="input">
                <option value="independente">Independente</option>
                <option value="bengala">Bengala</option>
                <option value="andador">Andador</option>
                <option value="cadeira_rodas">Cadeira de rodas</option>
                <option value="outro">Outro</option>
              </select>
            </F>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.needs_companion} onChange={(e) => set("needs_companion", e.target.checked)} data-testid="fp-transp-companion" />
              <span>Precisa de acompanhante durante o transporte</span>
            </label>
          </>
        )}

        {kind === "acompanhante" && (
          <>
            <F label="Local do atendimento *"><input value={form.location} onChange={(e) => set("location", e.target.value)} required data-testid="fp-acomp-location" placeholder="Nome/endereço da clínica ou hospital" className="input" /></F>
            <div className="grid sm:grid-cols-2 gap-4">
              <F label="Data *"><input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} min={new Date().toISOString().slice(0, 10)} required data-testid="fp-acomp-date" className="input" /></F>
              <F label="Horário *"><input type="time" value={form.time} onChange={(e) => set("time", e.target.value)} required data-testid="fp-acomp-time" className="input" /></F>
            </div>
            <F label="Tempo estimado (minutos)"><input type="number" min="15" step="15" value={form.estimated_duration_min} onChange={(e) => set("estimated_duration_min", e.target.value)} placeholder="60" data-testid="fp-acomp-duration" className="input" /></F>
          </>
        )}

        <F label="Observações"><textarea rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid={`fp-${kind}-notes`} placeholder="Contexto ou informações relevantes" className="input" /></F>

        <button type="submit" disabled={saving} data-testid={`fp-${kind}-submit`}
          className="w-full bg-[#0B2B62] text-white py-3 rounded-lg hover:bg-slate-800 disabled:opacity-50 font-medium inline-flex items-center justify-center gap-2">
          {saving ? "Enviando..." : <>Solicitar <ArrowRight className="w-4 h-4" /></>}
        </button>
      </form>
      <style>{`.input { width:100%; padding:0.5rem 0.75rem; border:1px solid #cbd5e1; border-radius:0.375rem; font-size:0.875rem; }`}</style>
    </div>
  );
}

const F = ({ label, children }) => (
  <label className="block">
    <div className="text-sm font-medium text-slate-700 mb-1">{label}</div>
    {children}
  </label>
);
